import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Descriptions, Button, Space, Tag, Spin, Alert, Modal, message } from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getActivity, deleteActivity, type Activity } from '@/api/activityApi';
import { getCustomer } from '@/api/customerApi';
import { getContact } from '@/api/contactApi';
import { useAuth } from '@/hooks/useAuth';

interface RelatedEntityInfo {
  name: string;
  link: string;
}

async function resolveRelatedEntity(
  relatedToType: string,
  relatedToId: string
): Promise<RelatedEntityInfo> {
  try {
    switch (relatedToType) {
      case 'customer': {
        const c = await getCustomer(relatedToId);
        return { name: c.name, link: `/customers/${relatedToId}` };
      }
      case 'contact': {
        const c = await getContact(relatedToId);
        return { name: c.name, link: `/contacts/${relatedToId}` };
      }
      case 'opportunity': {
        // Use dynamic import to avoid circular dependency
        const { getOpportunity } = await import('@/api/opportunityApi');
        const o = await getOpportunity(relatedToId);
        return { name: o.name, link: `/opportunities/${relatedToId}` };
      }
      default:
        return { name: relatedToId, link: '#' };
    }
  } catch {
    return { name: relatedToId, link: '#' };
  }
}

export default function ActivityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [activity, setActivity] = useState<Activity | null>(null);
  const [relatedEntity, setRelatedEntity] = useState<RelatedEntityInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canWrite = hasPermission('activities', 'write');
  const canDelete = hasPermission('activities', 'delete');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getActivity(id)
      .then((a) => {
        setActivity(a);
        if (a.related_to_type && a.related_to_id) {
          resolveRelatedEntity(a.related_to_type, a.related_to_id)
            .then(setRelatedEntity);
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const handleDelete = () => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('activities.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteActivity(id);
          message.success(t('activities.delete_success'));
          navigate('/activities');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (error || !activity) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/activities')}>
          {t('app.back')}
        </Button>
        <Alert type="error" message={error || t('app.not_found')} style={{ marginTop: 16 }} />
      </div>
    );
  }

  const formatDate = (val: string | null) => (val ? new Date(val).toLocaleString() : '-');

  const statusColorMap: Record<string, string> = {
    planned: 'blue',
    in_progress: 'orange',
    completed: 'green',
    cancelled: 'default',
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/activities')}>
          {t('app.back')}
        </Button>
        {canWrite && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/activities/${id}/edit`)}
          >
            {t('app.edit')}
          </Button>
        )}
        {canDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
      </Space>

      <h2>{t('activities.detail_title')}</h2>

      <Descriptions bordered column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label={t('activities.field.subject')}>{activity.subject}</Descriptions.Item>
        <Descriptions.Item label={t('activities.field.type')}>
          <Tag color={{ call: 'blue', email: 'purple', meeting: 'green', note: 'gold', task: 'cyan' }[activity.type] ?? 'default'}>
            {t(`activities.type_options.${activity.type}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('activities.field.status')}>
          <Tag color={statusColorMap[activity.status] ?? 'default'}>
            {t(`activities.status_options.${activity.status}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('activities.field.related_to_type')}>
          {t(`activities.related_to_options.${activity.related_to_type}`)}
        </Descriptions.Item>
        <Descriptions.Item label={t('activities.field.related_to_id')}>
          {relatedEntity ? (
            <Link to={relatedEntity.link}>{relatedEntity.name}</Link>
          ) : (
            activity.related_to_id
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('activities.field.owner_id')}>{activity.owner_id || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('activities.field.scheduled_at')}>{formatDate(activity.scheduled_at)}</Descriptions.Item>
        <Descriptions.Item label={t('activities.field.completed_at')}>{formatDate(activity.completed_at)}</Descriptions.Item>
        <Descriptions.Item label={t('activities.field.duration')}>
          {activity.duration > 0 ? `${activity.duration} ${t('activities.duration_unit')}` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('activities.field.branch_id')}>{activity.branch_id || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('activities.field.content')} span={2}>
          {activity.content || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('activities.field.notes')} span={2}>
          {activity.notes || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('activities.field.tags')}>
          {(activity.tags?.length ?? 0) > 0
            ? activity.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('activities.field.created_by')}>{activity.created_by || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('activities.field.created_at')}>{formatDate(activity.created_at)}</Descriptions.Item>
        <Descriptions.Item label={t('activities.field.updated_by')}>{activity.updated_by || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('activities.field.updated_at')}>{formatDate(activity.updated_at)}</Descriptions.Item>
      </Descriptions>
    </div>
  );
}
